<script setup lang="ts">
    import { computed, onMounted } from "vue";
    import { useI18n } from "vue-i18n";
    import { useLocale } from "@/composables/useLocale";
    import type { NavItem } from "@/types/content";

    const { t } = useI18n();
    const { currentLocale, setLocale } = useLocale();

    const navItems = computed<NavItem[]>(() => [
        { title: t("nav.home"), to: "/" },
        { title: t("nav.gallery"), to: "/gallery" },
        { title: t("nav.updates"), to: "/updates" },
        //{ title: t("nav.help"), to: "/help" },
        { title: t("nav.operation"), to: "/operation" },
        { title: t("nav.contacts"), to: "/contacts" },
    ]);

    const localeLabel = computed(() => currentLocale.value.toUpperCase());

    onMounted(() => {
        const state = history.state;
        if (state?.lang === "ru" || state?.lang === "uk") {
            setLocale(state.lang);
        }
    });
</script>

<template>
    <v-app-bar height="76" class="border-b border-slate-200 bg-white/90 backdrop-blur" flat>
        <v-container class="max-w-7xl">
            <div class="flex items-center justify-between gap-4">
                <RouterLink to="/" class="flex items-center gap-3">
                    <div class="flex h-full w-16 items-center justify-center rounded-2xl">
                        <v-img src="/img/logo.png" />
                    </div>

                    <div class="min-w-0">
                        <div class="text-base font-semibold tracking-tight text-slate-900">unilib</div>
                        <div class="text-xs text-slate-500">
                            {{ t("layout.brand_subtitle") }}
                        </div>
                    </div>
                </RouterLink>

                <div class="hidden items-center gap-1 lg:flex">
                    <RouterLink
                        v-for="item in navItems"
                        :key="item.to"
                        :to="item.to"
                        class="rounded-xl px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-950"
                    >
                        {{ item.title }}
                    </RouterLink>
                </div>

                <div class="hidden items-center gap-2 lg:flex">
                    <v-btn variant="text" @click="setLocale(currentLocale === 'uk' ? 'ru' : 'uk')">
                        {{ localeLabel }}
                    </v-btn>

                    <v-btn to="/contacts" color="primary" variant="flat" size="large">
                        {{ t("layout.cta") }}
                    </v-btn>
                </div>

                <div class="lg:hidden">
                    <v-menu location="bottom end">
                        <template #activator="{ props }">
                            <v-btn v-bind="props" icon="mdi-menu" variant="text" />
                        </template>

                        <v-list rounded="xl" class="min-w-64">
                            <v-list-item v-for="item in navItems" :key="item.to" :to="item.to" :title="item.title" />
                            <v-divider class="my-2" />
                            <v-list-item @click="setLocale(currentLocale === 'uk' ? 'ru' : 'uk')">
                                <v-list-item-title>{{ t("layout.switch_language") }}: {{ localeLabel }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
        </v-container>
    </v-app-bar>
</template>
